import { useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Camera, Palette } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DrawnFace, PromoImg } from './PromoImg';
import { SamplePhotosTag } from './SectionHeading';
import { APPROVED_DRAWING, DRAWN_FACES, SAMPLE_NAMES, SAMPLE_PHOTOS, type DrawnPerson } from './promo';

interface FlipPhotoProps {
  person: DrawnPerson;
  className?: string;
}

/**
 * A sample photo on a little print. Tap it (or point at it with a mouse) and
 * it crossfades to the same person's face as the storybook drew it, cut from
 * the cover next to it (the owner-approved drawing, promo.ts). The drawn
 * close-up is only fetched once someone reaches for the photo, and it fades
 * in only once it has loaded; with reduced motion it simply swaps.
 */
function FlipPhoto({ person, className }: FlipPhotoProps) {
  const { t } = useTranslation();
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [armed, setArmed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { image: drawing, face } = DRAWN_FACES[person];
  const drawn = (pressed || hovered) && loaded;
  const name = SAMPLE_NAMES[person];

  const onPointerEnter = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    setArmed(true);
    setHovered(true);
  };
  const onPointerLeave = (event: PointerEvent) => {
    if (event.pointerType === 'mouse') setHovered(false);
  };

  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={t('storybookPage.hero.visual.flipAria', { name })}
      onClick={() => {
        setArmed(true);
        setPressed((value) => !value);
      }}
      onFocus={() => setArmed(true)}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      className={cn(
        'group block rounded-xl bg-white p-1.5 pb-1 text-left shadow-[0_10px_24px_rgba(32,41,74,0.2)] ring-1 ring-black/5 transition-transform duration-300 hover:-translate-y-0.5 motion-reduce:transition-none',
        className,
      )}
    >
      <span className="relative block aspect-square overflow-hidden rounded-lg bg-muted">
        <PromoImg
          image={SAMPLE_PHOTOS[person]}
          sizes="(min-width: 640px) 150px, 28vw"
          eager
          alt=""
          className={cn(
            'absolute inset-0 h-full w-full object-cover transition-opacity duration-500 motion-reduce:transition-none',
            drawn && 'opacity-0',
          )}
        />
        {armed && (
          <DrawnFace
            image={drawing}
            face={face}
            sizes="(min-width: 640px) 600px, 112vw"
            alt=""
            onLoad={() => setLoaded(true)}
            className={cn(
              'absolute inset-0 transition-opacity duration-500 motion-reduce:transition-none',
              drawn ? 'opacity-100' : 'opacity-0',
            )}
          />
        )}
        <span
          aria-hidden="true"
          className="absolute bottom-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-primary shadow-sm"
        >
          {drawn ? <Palette className="h-3.5 w-3.5" /> : <Camera className="h-3.5 w-3.5" />}
        </span>
      </span>
      <span className="storybook-display block truncate px-0.5 pt-1 text-center text-xs font-semibold text-[#20294a] sm:text-sm">
        {drawn ? t('storybookPage.hero.visual.inBook', { name }) : t(`storybookPage.promo.people.${person}`)}
      </span>
    </button>
  );
}

/**
 * The hero's picture of the whole product: two sample photos, an arrow, and
 * the storybook cover the engine drew from them (the one drawing the owner
 * approved for the site). It is a sample, not a free preview a parent got:
 * the badge says "Sample cover", and the caption says the title set over it
 * is only an example.
 */
export function StorybookHeroVisual() {
  const { t } = useTranslation();
  return (
    <figure className="relative mx-auto w-full max-w-[23rem] sm:max-w-md lg:max-w-[30rem]" aria-labelledby="storybook-hero-visual-caption">
      <div className="relative flex items-center">
        <div className="relative z-10 -mr-[9%] flex w-[33%] shrink-0 flex-col items-start gap-2.5">
          <SamplePhotosTag label={t('storybookPage.promo.samplePhotos')} className="ml-0.5" />
          <FlipPhoto person="mia" className="w-[92%] -rotate-3" />
          <FlipPhoto person="ana" className="ml-[8%] w-[92%] rotate-2" />
        </div>

        <div className="relative min-w-0 flex-1">
          <div aria-hidden="true" className="absolute inset-0 translate-x-2 translate-y-2 rounded-3xl bg-[#f3ead9] shadow-[0_10px_24px_rgba(32,41,74,0.18)]" />
          <div className="relative aspect-square overflow-hidden rounded-3xl bg-[linear-gradient(170deg,#8ecbe6_0%,#cde8f2_60%,#b9c85a_100%)] shadow-[0_20px_50px_rgba(32,41,74,0.28)] ring-1 ring-black/5">
            <PromoImg
              image={APPROVED_DRAWING}
              sizes="(min-width: 1024px) 390px, (min-width: 640px) 362px, 70vw"
              eager
              className="h-full w-full object-cover"
            />
            <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-primary shadow-sm">
              {t('storybookPage.hero.visual.sampleBadge')}
            </span>
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-4 pb-4 pl-[14%] pt-14 text-center text-white sm:pb-5">
              <p className="storybook-display text-base font-semibold leading-tight drop-shadow sm:text-2xl">
                {t('storybookPage.hero.visual.coverTitle')}
              </p>
              <p className="mt-0.5 text-xs font-medium text-white/90 sm:text-sm">{t('storybookPage.book.preview.starring', { name: SAMPLE_NAMES.mia })}</p>
            </div>
          </div>
          {/* Just right of the prints, so the flipped label ("Mia, en el
              libro") runs clear of it even at 360px. */}
          <span
            aria-hidden="true"
            className="absolute left-[16%] top-1/2 z-20 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[3px] border-white bg-primary text-primary-foreground shadow-lg sm:h-12 sm:w-12"
          >
            <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </span>
        </div>
      </div>
      <figcaption id="storybook-hero-visual-caption" className="mt-4 text-center text-sm leading-snug text-muted-foreground sm:mt-6">
        {t('storybookPage.hero.visual.caption')}
        <span className="mt-1 block text-xs font-semibold text-primary">{t('storybookPage.hero.visual.flipHint')}</span>
      </figcaption>
    </figure>
  );
}
